/**
 * SYSTEM ITEMS — Open Food and Open Drink.
 *
 * WHAT THEY ARE. Two real `items` documents per restaurant that back the POS's
 * open-item ring-up. A server types a name and a price at the till; everything
 * else the line needs — course, tax rates, sales category, prep stations —
 * comes from the item the operator configured in the management app.
 *
 * WHY THEY ARE REAL DOCUMENTS AND NOT A CONSTANT. Before this, the POS's open
 * item was a bare name+price with `isTaxable: true` and no course, no sales
 * category and no prep station. So an open item could not be coursed, was
 * taxed by a blanket boolean rather than the restaurant's actual tax rates,
 * and landed in no sales category — which means it was invisible to every
 * category report while still counting in the totals. Making them documents
 * moves those four decisions to the person who should be making them, once,
 * in the management app.
 *
 * THREE INVARIANTS, AND THE ONE THAT IS STRUCTURAL
 * ------------------------------------------------
 * 1. NOT ARCHIVABLE. Archiving one would remove the POS's ability to ring an
 *    open item with no way to get it back from the items list, which filters
 *    archived rows out by default. Enforced in {@link assertSystemItemMutation}.
 *
 * 2. NOT ASSIGNABLE TO A MENU GROUP. This is the structural one and it is
 *    doing more work than it looks. Menu publishing is a REACHABILITY WALK
 *    from menu groups (see `BRAND_ARCHITECTURE.md`), so an item that belongs
 *    to no group is unreachable — and therefore never published to any of the
 *    13 marketplaces, never shown on a customer-facing menu, and never on a
 *    menu board. "Internal POS tool" and "never leaks to DoorDash" are the
 *    SAME guarantee, not two that must be kept in step. Do not add a
 *    marketplace-side exclusion list; it would be a second copy of this rule
 *    that can drift.
 *
 * 3. NOT RENAMEABLE, NOT RETYPEABLE, NOT DELETABLE. The POS resolves them by
 *    `systemItemType`, never by name, so renaming is safe for the POS but
 *    confusing everywhere else; the type itself is identity.
 *
 * WHAT THE OPERATOR *CAN* CHANGE is deliberately everything that affects how
 * the rung-up line behaves: course, tax rates, tax inclusion, sales category,
 * prep stations, kitchen name and the POS colour. See
 * {@link SYSTEM_ITEM_EDITABLE_FIELDS}.
 */

/** The two system items. The value is the stable identity — never the name. */
export const SYSTEM_ITEM_TYPES = ['open_food', 'open_drink'] as const;

export type SystemItemType = (typeof SYSTEM_ITEM_TYPES)[number];

export function isSystemItemType(value: unknown): value is SystemItemType {
    return typeof value === 'string' && (SYSTEM_ITEM_TYPES as readonly string[]).includes(value);
}

/**
 * Shape a system item is seeded with.
 *
 * `pricingStrategy: 'open'` AND `openPrice: true` are both set on purpose:
 * `priceRangeCalculator` reads `pricingStrategy === 'open' || openPrice`, and
 * the marketplace transformers read `openPrice` alone. Setting only one leaves
 * half the stack thinking it is a fixed-price item.
 */
export interface SystemItemSeed {
    readonly systemItemType: SystemItemType;
    readonly name: string;
    readonly posName: string;
    readonly position: number;
}

export const SYSTEM_ITEM_SEEDS: readonly SystemItemSeed[] = [
    { systemItemType: 'open_food', name: 'Open Food', posName: 'Open Food', position: 9001 },
    { systemItemType: 'open_drink', name: 'Open Drink', posName: 'Open Drink', position: 9002 },
] as const;

/**
 * Fields the operator may change on a system item.
 *
 * A closed list rather than a deny-list: a new field added to `Item` later is
 * then NOT silently editable on a system item, which is the safe default. The
 * cost is that widening this is a deliberate act, which is the point.
 */
export const SYSTEM_ITEM_EDITABLE_FIELDS = [
    'courseId',
    'taxRateIds',
    'taxInclusion',
    'takeoutTaxException',
    'salesCategoryId',
    'prepStationIds',
    'kitchenName',
    'posColor',
    'posDescription',
    'updatedAt',
    'updatedBy',
] as const;

export type SystemItemEditableField = (typeof SYSTEM_ITEM_EDITABLE_FIELDS)[number];

/** Reasons a system-item mutation is refused. Stable codes — clients branch on these. */
export const SYSTEM_ITEM_REFUSALS = {
    archive: 'SYSTEM_ITEM_CANNOT_BE_ARCHIVED',
    deactivate: 'SYSTEM_ITEM_CANNOT_BE_DEACTIVATED',
    delete: 'SYSTEM_ITEM_CANNOT_BE_DELETED',
    assign: 'SYSTEM_ITEM_CANNOT_BE_ASSIGNED_TO_A_MENU_GROUP',
    rename: 'SYSTEM_ITEM_CANNOT_BE_RENAMED',
    retype: 'SYSTEM_ITEM_TYPE_IS_IMMUTABLE',
    field: 'SYSTEM_ITEM_FIELD_NOT_EDITABLE',
} as const;

export type SystemItemRefusal = (typeof SYSTEM_ITEM_REFUSALS)[keyof typeof SYSTEM_ITEM_REFUSALS];

/** Minimal shape this module needs; avoids a cycle with `Item`. */
interface SystemItemLike {
    readonly systemItemType?: SystemItemType | null;
    readonly name?: string;
    readonly isArchived?: boolean;
    readonly isActive?: boolean;
    readonly menuGroupPrices?: readonly unknown[];
}

export function isSystemItem(item: SystemItemLike | null | undefined): boolean {
    return !!item && isSystemItemType(item.systemItemType);
}

/**
 * Refusal code for a proposed change to [current], or null when it is allowed.
 *
 * Returns the FIRST violated rule rather than a list: the caller renders one
 * message, and a partially-applied mutation is not a thing here.
 *
 * Called on the server before any write. The management UI also hides these
 * controls, but hiding a control is not enforcement — a stale tab, a bulk
 * edit, or a direct API call all reach the same document.
 */
export function checkSystemItemMutation(
    current: SystemItemLike,
    next: Partial<SystemItemLike>,
): SystemItemRefusal | null {
    if (!isSystemItem(current)) return null;

    if (next.isArchived === true) return SYSTEM_ITEM_REFUSALS.archive;
    if (next.isActive === false) return SYSTEM_ITEM_REFUSALS.deactivate;
    if (next.menuGroupPrices !== undefined && next.menuGroupPrices.length > 0) {
        return SYSTEM_ITEM_REFUSALS.assign;
    }
    if (next.name !== undefined && next.name !== current.name) {
        return SYSTEM_ITEM_REFUSALS.rename;
    }
    if (next.systemItemType !== undefined && next.systemItemType !== current.systemItemType) {
        return SYSTEM_ITEM_REFUSALS.retype;
    }
    return null;
}

/** True when a system item may be deleted. It never may. */
export function canDeleteSystemItem(item: SystemItemLike): boolean {
    return !isSystemItem(item);
}
